import {CallbackPayload, ConnectWindowTimeout, SDKMode} from "@shared-library";
import {NGXLogger} from "ngx-logger";
import {catchError, filter, first, fromEvent, map, Observable, throwError, timeout, TimeoutError} from "rxjs";

// waitForPostMessageOrgConnectionOrTimeout will listen to the child popup window handler for events (via the postMessage API).
// it will close if the popup window sends  close message, an error message or the timeout is reached.
// A successful connection will return a CallbackPayload
//
// This function must be ".subscribed()" to work. If not, the handler will not be registered and messages will be ignored.
// errors will be thrown if the connection is not completed during the timeout period. An error will also be thrown if the payload is an error.
// new Error('{"error":"error_code","error_description":"error message"}')
// Use `var errData = JSON.parse(err.toString())` parse the JSON payload from errors generated by this function.
//
// It would be ideal if we could also listen to window close events when the user manually closed the window.
// However, due to browser cross-origin limitations, it is not possible to do this.
// see: https://stackoverflow.com/questions/5712195/how-to-listen-for-child-window-closing
export function waitForPostMessageOrgConnectionOrTimeout(logger: NGXLogger, openedWindow: Window, sdkMode?: SDKMode): Observable<CallbackPayload> {
  logger.info(`waiting for postMessage notification from popup window`)

  //listen to post message
  return fromEvent(window, 'message')
    .pipe(
      //throw an error if we wait more than 2 minutes (this will close the window)
      timeout(ConnectWindowTimeout) as any,

      filter((event: MessageEvent) => {
        logger.debug(`received postMessage event, must determine if this message is safe to process`, event)
        if((sdkMode == SDKMode.ReactNative && window.ReactNativeWebView) || (sdkMode == SDKMode.Flutter)){
          //if we're in a React Native WebView, we can't do much filtering, since the source and the origin are not set.
          //we can only filter by the data, which is a JSON string.

          //make sure the source and origin are not set, any other event should be ignored
          if(event.source || event.origin){
            logger.debug(`ignoring postMessage event from unknown source or origin. ${sdkMode} webview should be null for both`, event.source, event.origin)
            return false
          }

          return true
        } else {
          //this is not a React Native WebView, so we can filter the message as normal.

          //make sure we're only listening to events from the "opened" window.
          if(event.source != openedWindow){
            logger.debug(`ignoring postMessage event from unknown source window`, event.source)
            return false
          }

          //only allow messages from the fasten connect API (kaiser will send messages from a different origin, so we need to filter those out).
          if(
            event.origin != 'https://api.connect.fastenlabs.com' &&
            event.origin != 'https://api.connect.fastenhealth.com' &&
            event.origin != 'https://embed.connect.fastenlabs.com' &&
            event.origin != 'https://embed.connect.fastenhealth.com'
          ){
            logger.debug(`ignoring postMessage event from unknown origin`, event.origin)
            return false
          }
        }

        return true
      }),
      //after filtering, we should only have one event to handle.
      first(),
      map((event: any) => {
        logger.info(`received postMessage notification from popup window & sending acknowledgment`, event.data)

        // @ts-ignore
        // this will not be valid in React Native WebView, but we don't care about that.
        event?.source?.postMessage(JSON.stringify({close:true}), event.origin);

        let parsedEventData = JSON.parse(event.data)
        if(parsedEventData.error){
          throw new Error(event.data) //pass the string back as an error (event.data is a string)
        } else {
          return parsedEventData
        }
      }),
      catchError((err) => {
        openedWindow.self.close()
        if (err instanceof TimeoutError) {
          logger.warn(`timed out waiting for notification from popup (${ConnectWindowTimeout/1000}s), closing window`)
          throw new Error(JSON.stringify({error: 'timeout', error_description:'timed out waiting for notification from popup'}))
        } else {
          logger.error(`an error was sent from the popup, closing window`, err)
          throw err //rethrow Error as-is.
        }

        //this is only here because the typescript compiler complains otherwise. this is unreachable, errors are thrown above.
        return throwError(err)
      })
    )
}
